import { useState, useEffect, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, TrendingUp, Activity, Loader2 } from 'lucide-react';

const emptyPlan = {
  name: '', description: '', category: 'mixed', pricing_mode: 'static',
  static_apy_pct: 12, live_symbol: '', min_investment: 100, max_investment: 0,
  lock_in_days: 0, management_fee_pct: 1, risk_level: 'medium',
  icon_emoji: '📈', color: '#2563EB', status: 'active',
};

const riskColor = (r) => r === 'low' ? 'bg-green-100 text-green-700' : r === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
const statusColor = (s) => s === 'active' ? 'bg-emerald-100 text-emerald-700' : s === 'paused' ? 'bg-slate-100 text-slate-700' : 'bg-rose-100 text-rose-700';

export default function InvestmentPlansAdminPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPlan);
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await api.get('/investment-plans');
      setPlans(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const openNew = () => { setEditing(null); setForm(emptyPlan); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyPlan, ...p });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.pricing_mode === 'live' && !form.live_symbol.trim()) { toast.error('Live symbol is required'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/investment-plans/${editing.id}`, form); toast.success('Plan updated'); }
      else { await api.post('/investment-plans', form); toast.success('Plan created'); }
      setOpen(false); fetchPlans();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/investment-plans/${id}`); toast.success('Plan deleted'); fetchPlans(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="investment-plans-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-brand-text-primary">Investment Plans</h1>
          <p className="text-sm text-brand-text-secondary">Create and manage ETF-style investment funds that investors can subscribe to.</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary text-white rounded-lg" data-testid="new-plan-btn"><Plus size={14} className="mr-1" /> New Plan</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>
      ) : plans.length === 0 ? (
        <Card className="border-brand-border bg-white"><CardContent className="p-12 text-center text-brand-text-secondary">No plans yet. Click "New Plan" to create one.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(p => (
            <Card key={p.id} className="border-brand-border bg-white overflow-hidden" data-testid={`plan-card-${p.id}`}>
              <div className="h-1.5" style={{ background: p.color }} />
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center text-2xl" style={{ background: `${p.color}18` }}>{p.icon_emoji}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-brand-text-primary truncate">{p.name}</p>
                    <div className="flex gap-1.5 mt-0.5">
                      <Badge className={`text-[9px] border-0 ${statusColor(p.status)}`}>{p.status}</Badge>
                      <Badge className={`text-[9px] border-0 ${riskColor(p.risk_level)}`}>{p.risk_level} risk</Badge>
                      <Badge className="text-[9px] border-0 bg-slate-100 text-slate-700">{p.category}</Badge>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-brand-text-secondary line-clamp-2 mb-3 h-8">{p.description || '—'}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-brand-bg/50 rounded p-2">
                    <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Pricing</p>
                    <p className="font-mono font-bold text-brand-text-primary">{p.pricing_mode === 'live' ? `Live · ${p.live_symbol}` : `Static · ${p.static_apy_pct}% APY`}</p>
                  </div>
                  <div className="bg-brand-bg/50 rounded p-2">
                    <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Current NAV</p>
                    <p className="font-mono font-bold text-brand-primary">${Number(p.current_nav).toFixed(4)}</p>
                  </div>
                  <div className="bg-brand-bg/50 rounded p-2">
                    <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">AUM</p>
                    <p className="font-mono font-bold text-brand-text-primary">${Number(p.aum).toLocaleString()}</p>
                  </div>
                  <div className="bg-brand-bg/50 rounded p-2">
                    <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Investors</p>
                    <p className="font-mono font-bold text-brand-text-primary">{p.investor_count}</p>
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-3">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-7 text-xs" data-testid={`plan-edit-${p.id}`}><Pencil size={12} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id, p.name)} className="h-7 text-xs text-red-600" data-testid={`plan-delete-${p.id}`}><Trash2 size={12} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Investment Plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 text-sm" data-testid="plan-name" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="text-sm" /></div>
            <div><Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{['crypto','commodity','mixed','other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Risk Level</Label>
              <Select value={form.risk_level} onValueChange={v => setForm({...form, risk_level: v})}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{['low','medium','high'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Pricing Mode *</Label>
              <Select value={form.pricing_mode} onValueChange={v => setForm({...form, pricing_mode: v})}>
                <SelectTrigger className="h-9 text-sm" data-testid="plan-pricing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Static (fixed APY)</SelectItem>
                  <SelectItem value="live">Live (tracks a symbol)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.pricing_mode === 'static' ? (
              <div><Label className="text-xs">Static APY %</Label><Input type="number" step="0.1" value={form.static_apy_pct} onChange={e => setForm({...form, static_apy_pct: Number(e.target.value)})} className="h-9 text-sm font-mono" /></div>
            ) : (
              <div><Label className="text-xs">Live Symbol (BTC, XAUUSD…)</Label><Input value={form.live_symbol} onChange={e => setForm({...form, live_symbol: e.target.value.toUpperCase()})} placeholder="BTC" className="h-9 text-sm font-mono uppercase" data-testid="plan-live-symbol" /></div>
            )}
            <div><Label className="text-xs">Min Investment ($)</Label><Input type="number" value={form.min_investment} onChange={e => setForm({...form, min_investment: Number(e.target.value)})} className="h-9 text-sm font-mono" /></div>
            <div><Label className="text-xs">Max Investment ($) — 0 = no cap</Label><Input type="number" value={form.max_investment} onChange={e => setForm({...form, max_investment: Number(e.target.value)})} className="h-9 text-sm font-mono" /></div>
            <div><Label className="text-xs">Lock-in (days)</Label><Input type="number" value={form.lock_in_days} onChange={e => setForm({...form, lock_in_days: Number(e.target.value)})} className="h-9 text-sm font-mono" /></div>
            <div><Label className="text-xs">Management Fee %</Label><Input type="number" step="0.1" value={form.management_fee_pct} onChange={e => setForm({...form, management_fee_pct: Number(e.target.value)})} className="h-9 text-sm font-mono" /></div>
            <div><Label className="text-xs">Icon (emoji)</Label><Input value={form.icon_emoji} onChange={e => setForm({...form, icon_emoji: e.target.value})} className="h-9 text-sm text-center text-lg" /></div>
            <div><Label className="text-xs">Color</Label><Input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="h-9 w-full" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{['active','paused','closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-brand-primary text-white" data-testid="plan-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : null} {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
